/**
 * Which push providers a build runs, and which one owns the OS device token.
 *
 * The app is mid-migration from OneSignal to Customer.io. Both SDKs ship in the
 * binary; this config decides which ones are actually wired up at runtime, so the
 * migration is an env-var change plus (eventually) deleting one provider file —
 * not a rewrite of every call site.
 *
 * `EXPO_PUBLIC_PUSH_PROVIDERS` is a comma-separated list, e.g.:
 *   `onesignal,customerio`  today — OneSignal owns the token, bridged into Customer.io
 *   `customerio`            end state — OneSignal removed from the build
 *   `onesignal`             rollback — Customer.io off, no behaviour change from before
 */

export const PUSH_PROVIDER_IDS = ['onesignal', 'customerio'] as const

export type PushProviderId = (typeof PUSH_PROVIDER_IDS)[number]

/**
 * Analytics `source` for a notification tap. Pinned to the strings the existing
 * dashboards already query — do not rename these to match the provider ids.
 */
export const PUSH_ANALYTICS_SOURCE: Record<PushProviderId, string> = {
  onesignal: 'OneSignalPush',
  customerio: 'CustomerIOPush'
}

const DEFAULT_PROVIDERS: PushProviderId[] = ['onesignal', 'customerio']

export type PushProvidersResolution = {
  /** Providers to initialize, in init order. */
  enabled: PushProviderId[]
  /**
   * The provider that registers with APNs/FCM and drives the OS permission prompt.
   * Every other enabled provider receives that token via `acceptDeviceToken`.
   */
  tokenOwner: PushProviderId
  /** Set when the requested list had to be corrected; surfaced as a warn log on boot. */
  warning?: string
}

function isProviderId(value: string): value is PushProviderId {
  return (PUSH_PROVIDER_IDS as readonly string[]).includes(value)
}

/**
 * Parse and normalize `EXPO_PUBLIC_PUSH_PROVIDERS`.
 *
 * Two rules are enforced rather than trusted to the env var:
 *
 * 1. At least one provider stays enabled. An empty or all-garbage value falls back
 *    to the default instead of silently shipping a build with no push at all.
 * 2. If OneSignal is enabled it owns the token, whatever the order says. Its SDK
 *    registers for remote notifications on `initialize()` and cannot be handed a
 *    token fetched by someone else, so `customerio,onesignal` is not a real
 *    configuration — it would leave OneSignal holding a token Customer.io also
 *    thinks it owns. Customer.io can accept a foreign token; OneSignal cannot.
 */
export function resolvePushProviders(raw?: string): PushProvidersResolution {
  const requested = (raw ?? '')
    .split(',')
    .map((value) => value.trim().toLowerCase())
    .filter(Boolean)

  const unknown = requested.filter((value) => !isProviderId(value))
  const valid = requested.filter(isProviderId)
  // Dedupe while preserving the author's order.
  const enabled = valid.filter((value, index) => valid.indexOf(value) === index)

  const warnings: string[] = []
  if (unknown.length) {
    warnings.push(`ignored unknown push provider(s): ${unknown.join(', ')}`)
  }

  if (!enabled.length) {
    if (requested.length) {
      warnings.push(`no valid provider in "${raw}", falling back to ${DEFAULT_PROVIDERS.join(',')}`)
    }
    return {
      enabled: [...DEFAULT_PROVIDERS],
      tokenOwner: DEFAULT_PROVIDERS[0],
      warning: warnings.length ? warnings.join('; ') : undefined
    }
  }

  const tokenOwner = enabled.includes('onesignal') ? 'onesignal' : enabled[0]
  if (tokenOwner !== enabled[0]) {
    warnings.push(`OneSignal always owns the device token; ignoring "${enabled[0]}" as first provider`)
  }

  return { enabled, tokenOwner, warning: warnings.length ? warnings.join('; ') : undefined }
}

const resolution = resolvePushProviders(process.env.EXPO_PUBLIC_PUSH_PROVIDERS)

export const pushConfig = {
  enabledProviders: resolution.enabled,
  tokenOwner: resolution.tokenOwner,
  configWarning: resolution.warning,
  /** OneSignal app id. Hardcoded historically; env var wins so the migration can null it out. */
  oneSignalAppId: process.env.EXPO_PUBLIC_ONESIGNAL_APP_ID?.trim() || 'ee982b05-fba9-44b9-9ee3-eb432c40d1f2'
}

export function isPushProviderEnabled(id: PushProviderId) {
  return pushConfig.enabledProviders.includes(id)
}
